import React, { useState, useEffect } from "react";
import Header from "./Header";
import Footer from "./Footer";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DynamicTitle from "./DynamicTitle";
import { trackEvent, identifyNewUser } from "../utils/posthog";

function ResetPassword() {
  const [checkingToken, setCheckingToken] = useState(true);
  const [displayForm, setDisplayForm] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordMatchError, setPasswordMatchError] = useState("");
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isExpired, setIsExpired] = useState(false);
  const [resendingLink, setResendingLink] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const useQuery = () => {return new URLSearchParams(location.search);};
  const query = useQuery();
  const token = query.get("token");
  const email = query.get("email"); // Don't encode here, we'll encode when needed
  const emailEncoded = encodeURIComponent(email || "");

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.get(`/api/reset-password?token=${token}&email=${emailEncoded}`);
        setCheckingToken(false);
        setDisplayForm(true);
        setIsExpired(false);
      } catch (error) {
        setCheckingToken(false);
        const status = error.response?.status || 500;
        const errorMsg = error.response?.data?.error || error.message;
        setErrorMessage(handleError(status, errorMsg));
        // Only mark as expired if it's actually a 401 (expired/invalid token)
        setIsExpired(status === 401);
        console.error('Error checking token:', error);
      }
    };

    if (token && email) {
      fetchData();
    } else {
      setCheckingToken(false);
      setErrorMessage("Missing token or email. Please use the link from your email.");
      setIsExpired(true);
    }
  }, [email, emailEncoded, token]); 

  function handleError(code, errMsg){
    console.log("handleError invoked! Error Code:", code, "Message:", errMsg)
      if (code === 401){
        return "Your link has expired or is invalid. Please request a new link.";
      }
      if (code === 404){
        return "User not found. Please check your email address or request a new link.";
      }
      if (code === 400){
        return errMsg || "Invalid request. Please check your information and try again.";
      }
      if (code === 405){
        return "Request method not allowed. Please try again or contact support.";
      }
      if (code >= 400 && code < 500){
        return errMsg || "Your request could not be processed. Please try again.";
      }
      return "An error occurred. Please try again later.";
  }

  const handleResendMagicLink = async () => {
    if (!email) {
      toast.error("Email address is required to resend the magic link.");
      return;
    }

    setResendingLink(true);
    setResendSuccess(false);
    setErrorMessage("");

    try {
      const response = await axios.post("/api/send-magic-link", {
        email: email,
      });

      console.log("Response from server:", response.data);
      toast.success("Magic link sent! Please check your email.");
      setResendSuccess(true);
      setMessage("A new magic link has been sent to your email. Please check your inbox and click the link to continue.");
    } catch (error) {
      console.error('Failed to resend magic link:', error);
      const errorMsg = error.response?.data?.error || "Failed to send magic link. Please try again.";
      toast.error(errorMsg);
      setErrorMessage(errorMsg);
    } finally {
      setResendingLink(false);
    }
  };

  const validatePassword = (pwd) => {
    const passwordPattern =
      /^(?=.*[0-9])(?=.*[!@#$%^&*? ]).{8,}$/;
    console.log("This password is " + passwordPattern.test(pwd));
    return passwordPattern.test(pwd);
    // return true
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

      console.log(`Sending PUT request to /api/reset-password with token and email: ${email}`);

    // Validate password
    if (!validatePassword(password)) {
      setPasswordError(
        "Password must be at least 8 characters long and include a number and a symbol.",
      );
      return;
    } else {
      setPasswordError("");
    }

    if (password !== passwordConfirm) {
      setPasswordMatchError(
        "Passwords should match.",
      );
      return;
    } else {
      setPasswordMatchError("");
    }

    try {
      setDisplayForm(false)
      setMessage("Resetting password...")
      // Manually encode URL parameters to ensure proper encoding
      const encodedToken = encodeURIComponent(token || "");
      const encodedEmail = encodeURIComponent(email || "");
      const url = `/api/reset-password?token=${encodedToken}&email=${encodedEmail}`;
      console.log(`PUT request URL: ${url}`);
      // Try PUT first (semantically correct), fallback to POST if PUT fails
      let response;
      try {
        response = await axios.put(url, { password });
      } catch (putError) {
        if (putError.response?.status === 405) {
          // If PUT fails with 405, fallback to POST
          console.warn('PUT request failed with 405, falling back to POST');
          response = await axios.post(url, { password });
        } else {
          throw putError;
        }
      }

      console.log("Response from server:", response.data);
      toast.success(response.data.message);
      if (email) identifyNewUser(email);
      trackEvent('password_reset_completed');
      setMessage("Success! Taking you back to the login page...")
      setTimeout(() => navigate('/login'), 1500)
    } catch (error) {
      setDisplayForm(true)
      setMessage("")
      const status = error.response?.status || 500;
      const errorMsg = error.response?.data?.error || error.message;
      setErrorMessage(handleError(status, errorMsg));
      console.error('Password Reset Failed', error);
      toast.error(errorMsg || "Failed to reset password. Please try again.");
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <DynamicTitle>Reset Password</DynamicTitle>
      <Header />
      <main className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Welcome Section */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-text mb-2">
              Reset Your Password
            </h1>
            <p className="text-textSecondary">
              Enter your new password below
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white py-8 px-6 rounded-lg border border-tint">
            {checkingToken && (
              <div className="text-center py-4">
                <p className="text-textSecondary">Checking token...</p>
              </div>
            )}

            {message && (
              <div className="bg-statusOk/10 border border-statusOk/30 text-statusOk px-4 py-3 rounded-md mb-4">
                {message}
              </div>
            )}

            {errorMessage && (
              <div className="bg-statusError/10 border border-statusError/30 text-statusError px-4 py-3 rounded-md mb-4">
                {errorMessage}
                {isExpired && email && (
                  <div className="mt-3">
                    <button
                      onClick={handleResendMagicLink}
                      disabled={resendingLink}
                      className="text-sm font-medium text-accent hover:underline disabled:text-textSecondary/50 disabled:no-underline"
                    >
                      {resendingLink ? "Sending..." : "Resend Magic Link"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {resendSuccess && (
              <div className="bg-statusOk/10 border border-statusOk/30 text-statusOk px-4 py-3 rounded-md mb-4">
                {message || "A new magic link has been sent to your email. Please check your inbox."}
              </div>
            )}

            {displayForm && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="newPassword"
                    className="block text-sm font-medium text-text mb-1"
                  >
                    New Password
                  </label>
                  <input
                    id="newPassword"
                    type="password"
                    placeholder="Enter your new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 p-3 block w-full border border-tint rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/30 transition-colors"
                  />
                  {passwordError && (
                    <p className="mt-1 text-sm text-statusError">{passwordError}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-text mb-1"
                  >
                    Confirm New Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your new password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    className="mt-1 p-3 block w-full border border-tint rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/30 transition-colors"
                  />
                  {passwordMatchError && (
                    <p className="mt-1 text-sm text-statusError">{passwordMatchError}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full bg-accent hover:bg-accentDark text-white font-semibold py-3 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2"
                >
                  Reset Password
                </button>
              </form>
            )}

            {/* Links Section */}
            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/login')}
                className="text-sm text-textSecondary hover:text-accent transition-colors"
              >
                Back to Sign In
              </button>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="text-center">
            <p className="text-xs text-textSecondary">
              Secure • Private • Research-backed
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default ResetPassword;
